import { Box, Divider, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { type UseFormProps, useForm } from "react-hook-form"

import { ASSOCIATION_TYPE_LABELS, EQUIVALENCY_TYPE_LABELS } from "../constants"

import { StatusFilter } from "@/components/common/Filters/StatusFilter"
import { PRODUCT_STATUS_LABEL_MAP } from "@/constants/product"
import { AssosiatedProductType, ComputedProductStatus, EquivalencyType } from "@/graphql/codegen/graphql"

interface FormValues extends Record<string, unknown> {
  equivalentTypes: EquivalencyType[]
  productStatuses: ComputedProductStatus[]
  associationTypes: AssosiatedProductType[]
}

const FILTER_EQUIVALENCY_TYPES = [EquivalencyType.Chemical, EquivalencyType.Functional]

const FILTER_PRODUCT_STATUSES = [
  ComputedProductStatus.Active,
  ComputedProductStatus.PendingApproval,
  ComputedProductStatus.Discontinued,
]

const FILTER_ASSOCIATION_TYPES = [
  AssosiatedProductType.Competitor,
  AssosiatedProductType.Akrochem,
  AssosiatedProductType.NonAssosiated,
]

export const EquivalentProductGroupsFilter: FC = () => {
  const navigate = useNavigate({ from: "/app/admin/manage-products/equivalents" })
  const currentSearch = useSearch({ from: "/app/admin/manage-products/_layout/equivalents" })

  const defaultValues = {
    equivalentTypes: currentSearch?.equivalentTypes || [],
    productStatuses: currentSearch?.productStatuses || [],
    associationTypes: currentSearch?.associationTypes || [],
  }

  const methods = useForm<FormValues>({
    values: defaultValues,
    mode: "onChange",
  } as UseFormProps<FormValues>)

  const handleFilterChange = <K extends keyof FormValues>(
    name: K,
    value: FormValues[K] | (FormValues[K] extends readonly (infer U)[] ? U : never)
  ) => {
    const normalizedValue = Array.isArray(value) ? value : [value]
    navigate({
      search: (prev) => ({
        ...prev,
        [name]: normalizedValue.length > 0 ? normalizedValue : undefined,
      }),
      replace: true,
    })
  }

  return (
    <Box className='h-full min-w-80'>
      <Box className='flex flex-1 flex-col'>
        <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
          Product Equivalent Type
        </Typography>
        <Divider />
        <StatusFilter<FormValues, EquivalencyType, "equivalentTypes">
          name='equivalentTypes'
          data={FILTER_EQUIVALENCY_TYPES}
          control={methods.control}
          onFilterChange={handleFilterChange}
          labelMap={EQUIVALENCY_TYPE_LABELS}
        />
      </Box>

      <Box className='flex flex-1 flex-col'>
        <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
          Product Status
        </Typography>
        <Divider />
        <StatusFilter<FormValues, ComputedProductStatus, "productStatuses">
          name='productStatuses'
          data={FILTER_PRODUCT_STATUSES}
          control={methods.control}
          onFilterChange={handleFilterChange}
          labelMap={PRODUCT_STATUS_LABEL_MAP}
        />
      </Box>

      <Box className='flex flex-1 flex-col'>
        <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
          Product Association Type
        </Typography>
        <Divider />
        <StatusFilter<FormValues, AssosiatedProductType, "associationTypes">
          name='associationTypes'
          data={FILTER_ASSOCIATION_TYPES}
          control={methods.control}
          onFilterChange={handleFilterChange}
          labelMap={ASSOCIATION_TYPE_LABELS}
        />
      </Box>
    </Box>
  )
}
